import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { apiErrorStatus } from '@/lib/api-error-code';
import { useInventory } from '@/arche/arches/dfe/hooks/useInventory';
import { isGameStateFlagSatisfied } from '../../types';
import type { LLTaskDefinition, LLTaskSubmission } from '../../types';

interface GateResult {
  canSubmit: boolean;
  loading: boolean;
  /** Why the gate isn't satisfied — surface as a tooltip when canSubmit is false. */
  reason: string | null;
}

const TWEET_HOSTS = new Set(['x.com', 'twitter.com', 'www.x.com', 'www.twitter.com']);
const TWEET_PATH_PATTERN = /^\/[^/]+\/status\/\d+/;

/**
 * Returns whether the player has filled in everything required by the
 * definition AND whether the async cross-arche checks (inventory,
 * game-state) report satisfied. The server re-validates on submit.
 *
 * Shape note: this hook makes one query per `checkInventory` element
 * (deduped by item name via React Query) and one query per
 * `checkGameState` element. With ~5 such elements per task the cost is
 * negligible; with 50+ this would warrant a batched endpoint.
 */
export function useDefinitionGate(
  definition: LLTaskDefinition,
  submission: LLTaskSubmission,
): GateResult {
  // Memoize the per-definition derivations so the array identities only change
  // when the definition itself changes. Otherwise React Query sees a fresh
  // queryKey on every render and refetches the inventory + game-state queries.
  const inventoryItems = useMemo(
    () =>
      Array.from(
        new Set(
          definition.elements
            .filter(
              (el): el is Extract<typeof el, { kind: 'checkInventory' }> =>
                el.kind === 'checkInventory',
            )
            .map((el) => el.item.toLowerCase()),
        ),
      ),
    [definition.elements],
  );

  // Keyed by `gameKey` alone: that IS the row keyword. Several elements on one
  // task typically read different fields of the SAME row, so deduping by key
  // also collapses them into one query.
  const gameStateKeywords = useMemo(
    () =>
      Array.from(
        new Set(
          definition.elements
            .filter(
              (el): el is Extract<typeof el, { kind: 'checkGameState' }> =>
                el.kind === 'checkGameState',
            )
            .map((el) => el.gameKey),
        ),
      ),
    [definition.elements],
  );

  // One getGameState query per distinct keyword.
  const gameStateQueries = useQueries({
    queries: gameStateKeywords.map((kw) => ({
      queryKey: queryKeys.gameState.detail(kw),
      queryFn: async () => {
        try {
          return await api.getGameState(kw);
        } catch (err) {
          if (apiErrorStatus(err) === 404) {
            return null;
          }
          throw err;
        }
      },
      retry: false,
      staleTime: 30_000,
    })),
  });

  // Single inventory load — we filter locally per-item. Searching by exact name
  // would require N parallel queries; one full-page load and an in-memory
  // filter is cheaper for a typical small inventory.
  const inventoryNeeded = inventoryItems.length > 0;
  const { items: inventoryEntries, loading: inventoryLoading } = useInventory(
    inventoryNeeded ? {} : { search: '__noop__' },
  );

  const inventoryByName = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of inventoryEntries) {
      const k = entry.name.toLowerCase();
      map.set(k, (map.get(k) ?? 0) + (entry.quantity ?? 1));
    }
    return map;
  }, [inventoryEntries]);

  const asyncLoading =
    (inventoryNeeded && inventoryLoading) || gameStateQueries.some((q) => q.isLoading);

  // Local-state gates first. These return a reason the moment they fail.
  for (const el of definition.elements) {
    if (el.kind === 'checkbox') {
      if (!submission.checkboxes.includes(el.index)) {
        return { canSubmit: false, loading: asyncLoading, reason: `Tick "${el.text}"` };
      }
    } else if (el.kind === 'multipleChoice') {
      const ans = submission.multipleChoices.find((m) => m.index === el.index);
      if (!ans) {
        return { canSubmit: false, loading: asyncLoading, reason: `Answer "${el.prompt}"` };
      }
      if (!el.options.some((o) => o.optionId === ans.optionId)) {
        return {
          canSubmit: false,
          loading: asyncLoading,
          reason: `Pick a valid option for "${el.prompt}"`,
        };
      }
    } else if (el.kind === 'textInput') {
      const v = submission.textInputs.find((t) => t.responseTag === el.responseTag)?.value ?? '';
      if (v.trim().length === 0) {
        return { canSubmit: false, loading: asyncLoading, reason: `Fill in "${el.label}"` };
      }
      if (el.inputType === 'tweet') {
        let parsed: URL;
        try {
          parsed = new URL(v.trim());
        } catch {
          return {
            canSubmit: false,
            loading: asyncLoading,
            reason: `"${el.label}" must be a URL`,
          };
        }
        if (!TWEET_HOSTS.has(parsed.host.toLowerCase())) {
          return {
            canSubmit: false,
            loading: asyncLoading,
            reason: `"${el.label}" must link to x.com or twitter.com`,
          };
        }
        if (!TWEET_PATH_PATTERN.test(parsed.pathname)) {
          return {
            canSubmit: false,
            loading: asyncLoading,
            reason: `"${el.label}" must point at a specific tweet (/<handle>/status/<id>)`,
          };
        }
      }
    }
  }

  // Async gates last — local issues should surface first because they're
  // immediately actionable.
  if (asyncLoading) {
    return { canSubmit: false, loading: true, reason: null };
  }

  for (const el of definition.elements) {
    if (el.kind === 'checkInventory') {
      const have = inventoryByName.get(el.item.toLowerCase()) ?? 0;
      if (have < el.amount) {
        return {
          canSubmit: false,
          loading: false,
          reason: `Need ${el.amount} ${el.item} (you have ${have})`,
        };
      }
    } else if (el.kind === 'checkGameState') {
      const idx = gameStateKeywords.indexOf(el.gameKey);
      const found = idx >= 0 ? gameStateQueries[idx]?.data : null;
      if (!isGameStateFlagSatisfied(found?.gameState?.data, el.gameState)) {
        return {
          canSubmit: false,
          loading: false,
          reason: `Game-state not yet recorded: ${el.gameKey}.${el.gameState}`,
        };
      }
    }
  }

  return { canSubmit: true, loading: false, reason: null };
}
